import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useTranslation } from 'react-i18next';
import { useClerk } from '@clerk/clerk-react';
import logo from '../assets/logo_without_text.png';
import { LayoutDashboard, Package, Store, Bell, LogOut, Settings, Moon, Sun, PanelLeft, User, Tag, ChevronDown, Shield } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useTheme } from '../contexts/ThemeProvider';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axiosInstance from '../utils/axiosInstance';

const Sidebar = ({ onCollapseChange, collapsed: externalCollapsed }) => {
  const { t } = useTranslation('sidebar');
  const { logout, user, changeBrand } = useAuth();
  const { signOut: clerkSignOut } = useClerk();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Brand selection state
  const [brandLoading, setBrandLoading] = useState(false);
  const [allBrands, setAllBrands] = useState(() => {
    // Initialize from localStorage cache to prevent flash
    const cached = localStorage.getItem('cachedBrands');
    return cached ? JSON.parse(cached) : [];
  });

  // For staff users, fetch all brands; for regular users, use allowed_brands
  const availableBrands = user?.is_staff ? allBrands : (user?.allowed_brands || []);

  // Fetch all brands for staff users (with cache - brands don't change often)
  useEffect(() => {
    if (user?.is_staff) {
      const cached = localStorage.getItem('cachedBrands');
      const cacheTime = localStorage.getItem('cachedBrandsTime');
      const CACHE_DURATION = 1000 * 60 * 60; // 1 hour cache

      // Use cache if valid and not expired
      if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < CACHE_DURATION) {
        setAllBrands(JSON.parse(cached));
        return;
      }

      // Fetch fresh data
      axiosInstance.get('/api/brands/')
        .then(res => {
          const brands = res.data.brands || [];
          setAllBrands(brands);
          localStorage.setItem('cachedBrands', JSON.stringify(brands));
          localStorage.setItem('cachedBrandsTime', Date.now().toString());
        })
        .catch(err => console.error('Failed to fetch brands:', err));
    }
  }, [user?.is_staff]);

  const handleBrandChange = async (newBrand) => {
    if (newBrand === user?.brand) return;
    setBrandLoading(true);
    const result = await changeBrand(newBrand);
    setBrandLoading(false);
    // Data will reload automatically because Products.jsx watches user.brand
  };

  // Use external collapsed state if provided, otherwise use localStorage
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const collapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  const handleToggle = () => {
    const newCollapsed = !collapsed;
    if (externalCollapsed === undefined) {
      setInternalCollapsed(newCollapsed);
      localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
    }
    if (onCollapseChange) onCollapseChange(newCollapsed);
  };

  const handleLogout = async () => {
    // Sign out from Clerk first to prevent auto-reconnect
    try {
      await clerkSignOut();
    } catch (err) {
      console.error('Clerk signout error:', err);
    }
    // Then clear app auth
    logout();
  };
  const [menuOpen, setMenuOpen] = useState(false);

  const navigation = [
    // Dashboard commented out
    // { name: t('dashboard'), href: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
    { name: t('products'), href: '/products', icon: Package, key: 'products' },
    { name: t('resellers'), href: '/resellers', icon: Store, key: 'resellers' },
    { name: t('alerts'), href: '/alerts', icon: Bell, key: 'alerts' },
    // Admin link - only visible for staff users
    ...(user?.is_staff ? [{ name: t('admin'), href: '/admin', icon: Shield, key: 'admin' }] : []),
  ];

  const isActive = (href) => location.pathname === href;

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 flex flex-col z-30 ${
        collapsed ? 'w-20' : 'w-56'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {collapsed ? (
            <PanelLeft 
              className="h-6 w-6 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleToggle}
            />
          ) : (
            <>
              <Link 
                to="/products"
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <img 
                  src={logo} 
                  alt="Logo" 
                  className="h-8 w-8"
                />
                <span className="text-xl font-bold" style={{ color: '#fed14d' }}>Pricy</span>
              </Link>
              <PanelLeft 
                className="h-5 w-5 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleToggle}
              />
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 relative">
        {navigation.map((item, index) => {
          const Icon = item.icon;
          return (
            <React.Fragment key={item.key}>
              <Link
                to={item.href}
                className={`w-full flex items-center rounded-lg transition-colors ${
                  collapsed 
                    ? 'justify-center px-0 py-3' 
                    : 'gap-3 px-3 py-3'
                } ${
                  isActive(item.href)
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                title={collapsed ? item.name : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <span className="text-sm font-medium">{item.name}</span>
                )}
              </Link>
              
            </React.Fragment>
          );
        })}
      </nav>

      {/* Bottom section with Brand select, Settings, Theme toggle, and Logout */}
      <div className="p-4 border-t border-border space-y-2">
        {/* Brand selector - show if user has multiple brands or is staff */}
        {(availableBrands.length > 1 || user?.is_staff) && (
          <div className={`${collapsed ? 'px-0' : 'px-1'}`}>
            {collapsed ? (
              <button
                className="w-full flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors py-3"
                title={user?.brand || t('selectBrand')}
              >
                <Tag className="h-5 w-5 flex-shrink-0" />
              </button>
            ) : (
              <Select
                value={user?.brand || ''}
                onValueChange={handleBrandChange}
                disabled={brandLoading}
              >
                <SelectTrigger className="w-full h-10 bg-background">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder={t('selectBrand')} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableBrands.map((brand) => (
                    <SelectItem key={brand} value={brand}>
                      {brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        <Link
          to="/settings"
          className={`w-full flex items-center rounded-lg transition-colors ${
            collapsed 
              ? 'justify-center px-0 py-3' 
              : 'gap-3 px-3 py-3'
          } ${
            isActive('/settings')
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          title={collapsed ? t('settings') : undefined}
        >
          <Settings className="h-5 w-5 flex-shrink-0" />
          {!collapsed && (
            <span className="text-sm font-medium">{t('settings')}</span>
          )}
        </Link>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className={`w-full flex items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors ${
            collapsed 
              ? 'justify-center px-0 py-3' 
              : 'gap-3 px-3 py-3'
          }`}
          title={collapsed ? (theme === 'dark' ? t('lightMode') : t('darkMode')) : undefined}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Moon className="h-5 w-5 flex-shrink-0" />
          )}
          {!collapsed && (
            <span className="text-sm font-medium">{theme === 'dark' ? t('lightMode') : t('darkMode')}</span>
          )}
        </button>
        
        <DropdownMenu open={menuOpen} setOpen={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              type="button"
              className={`w-full flex items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors ${
                collapsed
                  ? 'justify-center px-0 py-3'
                  : 'justify-start gap-3 px-3 py-3'
              }`}
              title={collapsed ? (user?.email || user?.display_name || user?.full_name || 'Account') : undefined}
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.avatar_url} alt={user?.email || user?.display_name || 'User'} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {user?.email?.[0]?.toUpperCase() || user?.display_name?.[0]?.toUpperCase() || user?.full_name?.[0]?.toUpperCase() || <User className="h-4 w-4" />}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <span className="text-sm font-medium truncate max-w-[140px]">{user?.email || user?.display_name || user?.full_name || 'Account'}</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="right"
            sideOffset={8}
            className="left-full ml-2 top-1/2 -translate-y-1/2"
          >
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

    </div>
  );
};

export default Sidebar;